/**
 * PaymentProofPage - SPO付款凭证管理页面
 *
 * 功能：
 * 1. 付款信息表单输入（金额、日期、方式、流水号）
 * 2. 文件上传（支持多文件、拖拽上传）
 * 3. 预览已上传凭证
 * 4. 提交审核
 * 5. 审核状态显示
 * 6. 管理员审核功能
 *
 * 权限控制：
 * - 运营商/管理员：上传、编辑未审核的凭证
 * - 管理员：审核凭证
 * - 发行人/交易所：仅查看
 */

// @ts-nocheck - TODO: Fix type definitions for payment APIs
import React, { useState, useEffect } from 'react';
import {
  Card,
  Form,
  Input,
  InputNumber,
  Select,
  DatePicker,
  Upload,
  Button,
  Space,
  Alert,
  Descriptions,
  Modal,
  message,
  Spin,
  Timeline,
  Divider,
  Row,
  Col,
  Typography,
  Empty,
  Table,
  Image,
  Tag,
} from 'antd';
import {
  UploadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  FileImageOutlined,
  FilePdfOutlined,
  DeleteOutlined,
  EyeOutlined,
  InboxOutlined,
} from '@ant-design/icons';
import type { UploadFile, UploadProps } from 'antd';
import dayjs from 'dayjs';
import {
  useGetSPOQuery,
  useUploadPaymentProofMutation,
  useVerifyPaymentMutation,
  useGetPaymentStatusQuery,
} from '../../store/api/spoApi';
import { UnifiedStatusTag } from '../../components/unified';
import type { PaymentProof, PaymentStatus } from '../../types/spo';
import { useResolvedSpoId } from './hooks/useResolvedSpoId';

const { TextArea } = Input;
const { Option } = Select;
const { Title, Text, Paragraph } = Typography;
const { Dragger } = Upload;

interface PaymentProofPageProps {
  projectId?: string;
}

const PaymentProofPage: React.FC<PaymentProofPageProps> = ({
  projectId: propProjectId,
}) => {
  const { resolvedSpoId, isResolving, isResolveError } = useResolvedSpoId({
    projectId: propProjectId,
  });

  // ==================== State管理 ====================
  const [form] = Form.useForm();
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewImage, setPreviewImage] = useState('');
  const [verifyModalVisible, setVerifyModalVisible] = useState(false);
  const [verifyForm] = Form.useForm();

  // ==================== API Hooks ====================
  const {
    data: spoData,
    isLoading: spoLoading,
    error: spoError,
  } = useGetSPOQuery(resolvedSpoId!, {
    skip: !resolvedSpoId,
  });

  const {
    data: paymentStatusData,
    isLoading: paymentLoading,
    refetch: refetchPaymentStatus,
  } = useGetPaymentStatusQuery(resolvedSpoId!, {
    skip: !resolvedSpoId,
  });

  const [uploadPaymentProof, { isLoading: uploading }] =
    useUploadPaymentProofMutation();
  const [verifyPayment, { isLoading: verifying }] = useVerifyPaymentMutation();

  // ==================== 权限控制 ====================
  // TODO: 从Redux Store获取实际用户角色
  const userRole = 'operator';
  const canUpload = userRole === 'operator' || userRole === 'admin';
  // @ts-ignore - TODO: Replace with actual user role from Redux
  const canVerify = userRole === 'admin';
  const canEdit =
    (userRole === 'operator' || userRole === 'admin') &&
    paymentStatusData?.data?.payment?.status === 'PENDING';

  // ==================== 表单初始化 ====================
  useEffect(() => {
    // Payment信息在paymentStatusData中
    const payment = paymentStatusData?.data?.payment;
    if (payment) {
      form.setFieldsValue({
        amount: payment.amount || undefined,
        currency: payment.currency || 'USD',
        paymentDate: payment.paymentDate
          ? dayjs(payment.paymentDate)
          : undefined,
        paymentMethod: payment.paymentMethod || undefined,
        transactionId: payment.transactionId || undefined,
        notes: payment.notes || undefined,
      });
    }
  }, [paymentStatusData, form]);

  // ==================== 文件上传处理 ====================
  const uploadProps: UploadProps = {
    name: 'file',
    multiple: true,
    fileList,
    accept: 'image/*,.pdf',
    beforeUpload: file => {
      const isValidType =
        file.type.startsWith('image/') || file.type === 'application/pdf';
      if (!isValidType) {
        message.error('只能上传图片或PDF文件！');
        return Upload.LIST_IGNORE;
      }
      const isLt10M = file.size / 1024 / 1024 < 10;
      if (!isLt10M) {
        message.error('文件大小不能超过10MB！');
        return Upload.LIST_IGNORE;
      }
      return false; // 阻止自动上传，手动处理
    },
    onChange: info => {
      setFileList(info.fileList);
    },
    onRemove: file => {
      setFileList(prev => prev.filter(item => item.uid !== file.uid));
    },
    onPreview: async file => {
      if (!file.url && !file.preview) {
        // 对于本地文件，生成预览
        const reader = new FileReader();
        reader.readAsDataURL(file.originFileObj as File);
        reader.onload = () => {
          setPreviewImage(reader.result as string);
          setPreviewVisible(true);
        };
      } else {
        setPreviewImage(file.url || file.preview || '');
        setPreviewVisible(true);
      }
    },
  };

  // ==================== 提交付款凭证 ====================
  const handleSubmitPayment = async () => {
    try {
      const values = await form.validateFields();

      if (fileList.length === 0) {
        message.error('请至少上传一个付款凭证文件！');
        return;
      }

      // 构建请求数据 - 简化版（实际应上传文件到服务器获取URL）
      const paymentData = {
        proofDocumentUrl: 'https://example.com/proof.pdf', // TODO: 实际上传文件获取URL
        amount: values.amount,
        currency: values.currency,
        paymentDate: values.paymentDate.toISOString(),
        paymentMethod: values.paymentMethod,
        notes: values.notes,
      };

      const result = await uploadPaymentProof({
        spoId: resolvedSpoId!,
        data: paymentData as any, // TODO: Fix type definition
      }).unwrap();

      if (result.success) {
        message.success('付款凭证上传成功，等待管理员审核');
        refetchPaymentStatus();
        setFileList([]);
        form.resetFields();
      } else {
        message.error(result.error?.message || '上传失败');
      }
    } catch (error) {
      console.error('Submit payment proof error:', error);
      message.error('提交失败，请检查表单');
    }
  };

  // ==================== 审核付款凭证 ====================
  const handleVerifyPayment = async () => {
    try {
      const values = await verifyForm.validateFields();

      const result = await verifyPayment({
        spoId: resolvedSpoId!,
        approved: values.approved,
        notes: values.verificationNotes,
      }).unwrap();

      if (result.success) {
        message.success(
          values.approved ? '付款凭证审核通过' : '付款凭证已拒绝'
        );
        setVerifyModalVisible(false);
        verifyForm.resetFields();
        refetchPaymentStatus();
      } else {
        message.error(result.error?.message || '审核失败');
      }
    } catch (error) {
      console.error('Verify payment error:', error);
      message.error('审核失败');
    }
  };

  // ==================== 渲染付款状态 ====================
  const renderPaymentStatus = () => {
    if (!paymentStatusData?.data?.payment) {
      return (
        <Alert
          message="未上传付款凭证"
          description="请上传线下支付凭证以继续SPO流程"
          type="warning"
          showIcon
          icon={<ClockCircleOutlined />}
        />
      );
    }

    const payment = paymentStatusData.data.payment;

    return (
      <Card title="付款信息" style={{ marginBottom: 24 }}>
        <Descriptions column={2} bordered>
          <Descriptions.Item label="付款金额">
            {payment.amount} {payment.currency}
          </Descriptions.Item>
          <Descriptions.Item label="付款日期">
            {payment.paymentDate
              ? dayjs(payment.paymentDate).format('YYYY-MM-DD')
              : '-'}
          </Descriptions.Item>
          <Descriptions.Item label="付款方式">
            {payment.paymentMethod === 'bank_transfer'
              ? '银行转账'
              : payment.paymentMethod === 'check'
                ? '支票'
                : '其他'}
          </Descriptions.Item>
          <Descriptions.Item label="交易流水号">
            {payment.transactionId}
          </Descriptions.Item>
          <Descriptions.Item label="审核状态" span={2}>
            <UnifiedStatusTag type="paymentStatus" status={payment.status} />
          </Descriptions.Item>
          {payment.verifiedBy && (
            <>
              <Descriptions.Item label="审核人">
                {payment.verifiedBy}
              </Descriptions.Item>
              <Descriptions.Item label="审核日期">
                {payment.verifiedDate
                  ? dayjs(payment.verifiedDate).format('YYYY-MM-DD HH:mm')
                  : '-'}
              </Descriptions.Item>
            </>
          )}
          {payment.rejectionReason && (
            <Descriptions.Item label="拒绝原因" span={2}>
              <Text type="danger">{payment.rejectionReason}</Text>
            </Descriptions.Item>
          )}
          {payment.notes && (
            <Descriptions.Item label="备注说明" span={2}>
              {payment.notes}
            </Descriptions.Item>
          )}
        </Descriptions>

        {/* 已上传文件列表 */}
        {payment.proofDocuments && payment.proofDocuments.length > 0 && (
          <>
            <Divider>已上传凭证文件</Divider>
            <Row gutter={[16, 16]}>
              {payment.proofDocuments.map((doc, index) => (
                <Col key={index} xs={24} sm={12} md={8}>
                  <Card
                    size="small"
                    hoverable
                    cover={
                      doc.fileType === 'image' ? (
                        <Image
                          src={doc.fileUrl}
                          alt={doc.filename}
                          style={{ height: 150, objectFit: 'cover' }}
                          preview={{
                            mask: (
                              <Space>
                                <EyeOutlined /> 预览
                              </Space>
                            ),
                          }}
                        />
                      ) : (
                        <div
                          style={{
                            height: 150,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: '#f5f5f5',
                          }}
                        >
                          <FilePdfOutlined
                            style={{ fontSize: 48, color: '#1890ff' }}
                          />
                        </div>
                      )
                    }
                  >
                    <Card.Meta
                      title={doc.filename}
                      description={
                        <>
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            {dayjs(doc.uploadDate).format('YYYY-MM-DD HH:mm')}
                          </Text>
                          <br />
                          <a
                            href={doc.fileUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            下载文件
                          </a>
                        </>
                      }
                    />
                  </Card>
                </Col>
              ))}
            </Row>
          </>
        )}

        {/* 管理员审核按钮 */}
        {canVerify && payment.status === 'PENDING' && (
          <div style={{ marginTop: 16, textAlign: 'right' }}>
            <Button
              type="primary"
              icon={<CheckCircleOutlined />}
              onClick={() => setVerifyModalVisible(true)}
            >
              审核凭证
            </Button>
          </div>
        )}
      </Card>
    );
  };

  // ==================== 渲染上传表单 ====================
  const renderUploadForm = () => {
    if (!canUpload) {
      return (
        <Alert
          message="无上传权限"
          description="只有运营商和管理员可以上传付款凭证"
          type="info"
          showIcon
        />
      );
    }

    if (paymentStatusData?.data?.payment?.status === 'VERIFIED') {
      return (
        <Alert
          message="付款凭证已审核通过"
          description="凭证已通过审核，无需重新上传"
          type="success"
          showIcon
          icon={<CheckCircleOutlined />}
        />
      );
    }

    return (
      <Card title="上传付款凭证" style={{ marginBottom: 24 }}>
        <Alert
          message="上传提示"
          description="请在完成线下银行转账后，上传付款凭证（银行回单、转账截图等）"
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />

        <Form form={form} layout="vertical">
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                label="付款金额"
                name="amount"
                rules={[
                  { required: true, message: '请输入付款金额' },
                  { type: 'number', min: 0, message: '金额必须大于0' },
                ]}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  placeholder="请输入付款金额"
                  precision={2}
                  min={0}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                label="币种"
                name="currency"
                initialValue="USD"
                rules={[{ required: true, message: '请选择币种' }]}
              >
                <Select placeholder="请选择币种">
                  <Option value="USD">美元 (USD)</Option>
                  <Option value="CNY">人民币 (CNY)</Option>
                  <Option value="EUR">欧元 (EUR)</Option>
                  <Option value="GBP">英镑 (GBP)</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                label="付款日期"
                name="paymentDate"
                rules={[{ required: true, message: '请选择付款日期' }]}
              >
                <DatePicker
                  style={{ width: '100%' }}
                  placeholder="请选择付款日期"
                  format="YYYY-MM-DD"
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                label="付款方式"
                name="paymentMethod"
                initialValue="bank_transfer"
                rules={[{ required: true, message: '请选择付款方式' }]}
              >
                <Select placeholder="请选择付款方式">
                  <Option value="bank_transfer">银行转账</Option>
                  <Option value="check">支票</Option>
                  <Option value="other">其他</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            label="交易流水号"
            name="transactionId"
            rules={[{ required: true, message: '请输入交易流水号' }]}
          >
            <Input placeholder="请输入银行交易流水号" />
          </Form.Item>

          <Form.Item label="备注说明" name="notes">
            <TextArea
              rows={3}
              placeholder="请输入备注说明（选填）"
              maxLength={500}
              showCount
            />
          </Form.Item>

          <Form.Item label="上传凭证文件">
            <Dragger {...uploadProps}>
              <p className="ant-upload-drag-icon">
                <InboxOutlined />
              </p>
              <p className="ant-upload-text">点击或拖拽文件到此区域上传</p>
              <p className="ant-upload-hint">
                支持上传图片或PDF文件，单个文件不超过10MB
              </p>
            </Dragger>
          </Form.Item>

          <Form.Item>
            <Space>
              <Button
                type="primary"
                size="large"
                loading={uploading}
                onClick={handleSubmitPayment}
                disabled={fileList.length === 0}
              >
                提交付款凭证
              </Button>
              <Button
                size="large"
                onClick={() => {
                  form.resetFields();
                  setFileList([]);
                }}
              >
                重置表单
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>
    );
  };

  // ==================== Loading/Error处理 ====================
  if (isResolving || spoLoading || paymentLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px 0' }}>
        <Spin size="large" />
        <div style={{ marginTop: 12 }}>加载付款信息中...</div>
      </div>
    );
  }

  if (spoError || isResolveError || !resolvedSpoId) {
    return (
      <Alert
        message="加载失败"
        description="无法加载SPO流程信息，请刷新页面重试"
        type="error"
        showIcon
      />
    );
  }

  // ==================== 主渲染 ====================
  return (
    <div style={{ padding: '24px' }}>
      <Title level={2}>付款凭证管理</Title>
      <Paragraph type="secondary">
        请在完成线下支付后，上传付款凭证以继续SPO流程
      </Paragraph>

      <Divider />

      {/* 付款状态 */}
      {renderPaymentStatus()}

      {/* 上传表单 */}
      {renderUploadForm()}

      {/* 审核Modal */}
      <Modal
        title="审核付款凭证"
        open={verifyModalVisible}
        onOk={handleVerifyPayment}
        onCancel={() => {
          setVerifyModalVisible(false);
          verifyForm.resetFields();
        }}
        confirmLoading={verifying}
        okText="提交审核"
        cancelText="取消"
        width={600}
      >
        <Alert
          message="审核提示"
          description="请仔细核对付款凭证的真实性和完整性后再进行审核"
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
        />

        <Form form={verifyForm} layout="vertical">
          <Form.Item
            label="审核结果"
            name="approved"
            rules={[{ required: true, message: '请选择审核结果' }]}
          >
            <Select placeholder="请选择审核结果">
              <Option value={true}>
                <Space>
                  <CheckCircleOutlined style={{ color: '#52c41a' }} />
                  <span>审核通过</span>
                </Space>
              </Option>
              <Option value={false}>
                <Space>
                  <CloseCircleOutlined style={{ color: '#ff4d4f' }} />
                  <span>审核拒绝</span>
                </Space>
              </Option>
            </Select>
          </Form.Item>

          <Form.Item
            label="审核意见"
            name="verificationNotes"
            rules={[{ required: true, message: '请输入审核意见' }]}
          >
            <TextArea
              rows={4}
              placeholder="请输入审核意见（审核拒绝时必须说明原因）"
              maxLength={500}
              showCount
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* 图片预览Modal */}
      <Modal
        open={previewVisible}
        title="凭证预览"
        footer={null}
        onCancel={() => setPreviewVisible(false)}
        width={800}
      >
        <img alt="凭证" style={{ width: '100%' }} src={previewImage} />
      </Modal>
    </div>
  );
};

export default PaymentProofPage;
